import {
  Grid,
  Paper,
  Typography,
  Backdrop,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Button,
  ListItemText,
  OutlinedInput,
  Tabs,
  Tab,
  Box,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { useOktaAuth } from "@okta/okta-react";
import React, { useEffect, useState } from "react";
import {
  Formik,
  Form,
  FastField,
  FieldArray,
} from "formik";
import {
  ClientApi,
  FieldDto,
  FieldTypeUsageDto,
} from "../../../Api/SocketRulesEngineApi.generated";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import ToastAlert from "../../global/ToastAlert";
import SettingsIcon from "@mui/icons-material/Settings";
import InfoIcon from "@mui/icons-material/Info";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const Settings = ({ redirectPage }: any) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState<number>(0);

  const { oktaAuth } = useOktaAuth();
  const token = oktaAuth.getAccessToken();

  const [fields, setFields] = useState<FieldDto[]>([]);
  const [fieldTypes, setFieldTypes] = useState<string[]>([]);
  const [filteredTypes, setFilteredTypes] = useState<string[]>([]);
  const [filterText, setFilterText] = useState<string>("");
  const [typeUsage, setTypeUsage] = useState<FieldTypeUsageDto[]>([]);
  const [openToast, setToast] = useState<boolean>(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const clientApi = new ClientApi();
    const getProcessorFields = async () => {
      clientApi.token = token;
      let fields = await clientApi.getClientFields();
      return fields;
    };

    const getDataTypeUsage = async () => {
      clientApi.token = token;
      let usage = await clientApi.getFieldTypeUsage();
      return usage;
    };
    const fetchData = async () => {
      const fields = await getProcessorFields();
      const fieldTypeUsage = await getDataTypeUsage();
      setTypeUsage(fieldTypeUsage);
      updateDataFields(fields);
    };
    fetchData();
  }, [token]);

  const updateDataFields = (data: FieldDto[]) => {
    const types = [...new Set(data.map((d) => d.fieldType ?? ""))];
    setFieldTypes(types);
    setFields(data);
  };

  const saveChanges = async (updatedFields: FieldDto[]) => {
    const clientApi = new ClientApi();
    clientApi.token = token;

    setLoading(true);
    await clientApi
      .updateRuleProcessorFields(updatedFields)
      .then((result) => {
        updateDataFields(result);
        setLoading(false);
        setToast(true);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  const handleDataTypeChange = (
    event: SelectChangeEvent<typeof filteredTypes>
  ) => {
    const {
      target: { value },
    } = event;
    setFilteredTypes(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleFilterTextChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFilterText(event.target.value);
  };

  const filterRow = (fieldRow: FieldDto) => {
    // Filter based on type dropdown
    if (
      filteredTypes.length > 0 &&
      filteredTypes.indexOf(fieldRow.fieldType ?? "") === -1
    ) {
      return true;
    }

    // Filter based on text entry
    const filter = filterText.toLowerCase();
    if (
      fieldRow.displayName?.toLowerCase().indexOf(filter) === -1 &&
      fieldRow.fieldName?.toLowerCase().indexOf(filter) === -1
    ) {
      return true;
    }

    return false;
  };

  const enableCheckbox = (type: any, useIn: string): boolean => {
    let found = typeUsage.find((t) => t.fieldType === type);
    if (found !== undefined) {
      return found[useIn as keyof FieldTypeUsageDto] as boolean;
    }
    return false;
  };

  // Calculate field statistics
  const fieldStats = {
    total: fields.length,
    conditionFields: fields.filter((f) => f.allowedAsCondition).length,
    modificationFields: fields.filter((f) => f.allowedAsModification).length,
    uniqueTypes: fieldTypes.length,
  };

  return (
    <Grid item container xs={12} width="100%">
      <ToastAlert
        open={openToast}
        setToast={setToast}
        errorMsg={"Setting Changes have been Saved"}
      />
      <Paper
        elevation={10}
        sx={{
          padding: "40px 40px 30px 40px",
          width: "100%",
          minHeight: "calc(100vh - 120px)",
        }}
      >
        <Grid item container spacing={3}>
          {/* Header */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <SettingsIcon fontSize="large" color="primary" />
              <div>
                <Typography variant="h4">Admin Settings</Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure system settings and manage field definitions
                </Typography>
              </div>
            </Box>
          </Grid>

          {/* Tabs */}
          <Grid item xs={12}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab label="Field Configuration" />
                <Tab label="System Information" />
              </Tabs>
            </Box>
          </Grid>

          {/* Tab Panel 0: Field Configuration */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              {/* Statistics Cards */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={2}>
                      <CardContent sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="h4" color="primary.main">
                          {fieldStats.total}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Fields
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={2}>
                      <CardContent sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="h4" color="info.main">
                          {fieldStats.conditionFields}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Condition Fields
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={2}>
                      <CardContent sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="h4" color="secondary.main">
                          {fieldStats.modificationFields}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Modification Fields
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={2}>
                      <CardContent sx={{ textAlign: "center", py: 2 }}>
                        <Typography variant="h4" color="success.main">
                          {fieldStats.uniqueTypes}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Data Types
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Filter Controls */}
              <Grid item container xs={12} spacing={4}>
                <Grid item xs={12} md={6}>
                  <Tooltip
                    title="Filter available fields for the rule engine based on the Field or Display name."
                    placement="top"
                  >
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Filter by Name"
                      size="small"
                      value={filterText}
                      onChange={handleFilterTextChange}
                    />
                  </Tooltip>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Tooltip
                    title="Filter available fields for the rule engine based on the data type of the Field."
                    placement="top"
                  >
                    <FormControl fullWidth size="small">
                      <InputLabel id="data-type-select-label">
                        Data Type
                      </InputLabel>
                      <Select
                        labelId="data-type-select-label"
                        id="data-type-select"
                        multiple
                        value={filteredTypes}
                        onChange={handleDataTypeChange}
                        input={<OutlinedInput label="Data Type" />}
                        renderValue={(selected) => {
                          if (selected.length === 0) {
                            return <em>All Types</em>;
                          }
                          return selected.join(", ");
                        }}
                      >
                        {fieldTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            <Checkbox checked={filteredTypes.indexOf(type) > -1} />
                            <ListItemText primary={type} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Tooltip>
                </Grid>
              </Grid>

              {/* Fields Table */}
              <Grid item xs={12}>
            <Formik
              initialValues={{ fields: fields }}
              enableReinitialize
              validateOnChange={false}
              onSubmit={async (values, actions) => {
                const updatedFields = values.fields.filter((field, i) => {
                  return (
                    field.displayName !== fields[i].displayName ||
                    field.allowedAsCondition !== fields[i].allowedAsCondition ||
                    field.allowedAsModification !==
                      fields[i].allowedAsModification
                  );
                });

                saveChanges(updatedFields);
              }}
            >
              {(formik) => {
                const { handleChange, values } = formik;

                return (
                  <Form>
                    <Grid container rowSpacing={4}>
                      <Grid item xs={12}>
                        <TableContainer
                          component={Paper}
                          sx={{ maxHeight: 600 }}
                        >
                          <Table
                            stickyHeader
                            sx={{ minWidth: 650 }}
                            aria-label="simple table"
                          >
                            <TableHead>
                              <TableRow>
                                <StyledTableCell sx={{ maxWidth: 300 }}>
                                  Field
                                </StyledTableCell>
                                <StyledTableCell sx={{ width: 400 }}>
                                  Display
                                </StyledTableCell>
                                <StyledTableCell>Data Type</StyledTableCell>
                                <StyledTableCell>
                                  Use in Conditions
                                </StyledTableCell>
                                <StyledTableCell>
                                  Use in Modifications
                                </StyledTableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <FieldArray
                                name="fields"
                                render={(arrayHelpers: any) =>
                                  values.fields.map((row, index) => {
                                    if (filterRow(row)) {
                                      return null;
                                    } else {
                                      return (
                                        <TableRow
                                          key={index}
                                          sx={{
                                            "&:last-child td, &:last-child th":
                                              {
                                                border: 0,
                                              },
                                          }}
                                        >
                                          <TableCell component="th" scope="row">
                                            <Tooltip title={row.fieldName}>
                                              <span
                                                style={{
                                                  textOverflow: "ellipsis",
                                                  overflow: "hidden",
                                                  whiteSpace: "nowrap",
                                                  direction: "rtl",
                                                  display: "flow",
                                                  maxWidth: 400,
                                                }}
                                              >
                                                {row.fieldName}
                                              </span>
                                            </Tooltip>
                                          </TableCell>
                                          <TableCell>
                                            <TextField
                                              name={`fields.${index}.displayName`}
                                              value={row.displayName}
                                              variant="outlined"
                                              size="small"
                                              onChange={handleChange}
                                              sx={{ width: "100%" }}
                                            ></TextField>
                                          </TableCell>
                                          <TableCell>{row.fieldType}</TableCell>
                                          <TableCell>
                                            {!enableCheckbox(
                                              row?.fieldType,
                                              "forUseInConditions"
                                            ) ? (
                                              <Tooltip
                                                title="There are no operations available for this Data Type currently"
                                                arrow={true}
                                              >
                                                <div>
                                                  <FastField
                                                    type="checkbox"
                                                    name={`fields.${index}.allowedAsCondition`}
                                                    value={
                                                      row.allowedAsCondition
                                                    }
                                                    key={row.fieldId}
                                                    as={FormControlLabel}
                                                    control={<Checkbox />}
                                                    checked={
                                                      row.allowedAsCondition
                                                    }
                                                    label="Enabled"
                                                    disabled={
                                                      !enableCheckbox(
                                                        row?.fieldType,
                                                        "forUseInConditions"
                                                      )
                                                    }
                                                  />
                                                </div>
                                              </Tooltip>
                                            ) : (
                                              <FastField
                                                type="checkbox"
                                                name={`fields.${index}.allowedAsCondition`}
                                                value={row.allowedAsCondition}
                                                key={row.fieldId}
                                                as={FormControlLabel}
                                                control={<Checkbox />}
                                                checked={row.allowedAsCondition}
                                                label="Enabled"
                                                disabled={
                                                  !enableCheckbox(
                                                    row?.fieldType,
                                                    "forUseInConditions"
                                                  )
                                                }
                                              />
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {!enableCheckbox(
                                              row?.fieldType,
                                              "forUseInModifications"
                                            ) ? (
                                              <Tooltip
                                                title="There are no operations available for this Data Type currently"
                                                arrow={true}
                                              >
                                                <div>
                                                  <FastField
                                                    type="checkbox"
                                                    name={`fields.${index}.allowedAsModification`}
                                                    value={
                                                      row.allowedAsModification
                                                    }
                                                    key={row.fieldId}
                                                    as={FormControlLabel}
                                                    control={<Checkbox />}
                                                    checked={
                                                      row.allowedAsModification
                                                    }
                                                    label="Enabled"
                                                    disabled={
                                                      !enableCheckbox(
                                                        row?.fieldType,
                                                        "forUseInModifications"
                                                      )
                                                    }
                                                  />
                                                </div>
                                              </Tooltip>
                                            ) : (
                                              <FastField
                                                type="checkbox"
                                                name={`fields.${index}.allowedAsModification`}
                                                value={
                                                  row.allowedAsModification
                                                }
                                                key={row.fieldId}
                                                as={FormControlLabel}
                                                control={<Checkbox />}
                                                checked={
                                                  row.allowedAsModification
                                                }
                                                label="Enabled"
                                                disabled={
                                                  !enableCheckbox(
                                                    row?.fieldType,
                                                    "forUseInModifications"
                                                  )
                                                }
                                              />
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    }
                                  })
                                }
                              />
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                      <Grid
                        item
                        container
                        xs={12}
                        direction="row"
                        justifyContent="right"
                        alignItems="center"
                      >
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          sx={{
                            color: "white",
                          }}
                        >
                          Save Changes
                        </Button>
                      </Grid>
                    </Grid>
                  </Form>
                );
              }}
            </Formik>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab Panel 1: System Information */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <InfoIcon color="primary" />
                      <Typography variant="h6">System Information</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Total Fields
                        </Typography>
                        <Typography variant="h6">{fieldStats.total}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Unique Data Types
                        </Typography>
                        <Typography variant="h6">{fieldStats.uniqueTypes}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Fields Available for Conditions
                        </Typography>
                        <Typography variant="h6">
                          {fieldStats.conditionFields}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Fields Available for Modifications
                        </Typography>
                        <Typography variant="h6">
                          {fieldStats.modificationFields}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Data Type Distribution
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      {fieldTypes.map((type) => {
                        const count = fields.filter((f) => f.fieldType === type).length;
                        const percentage = ((count / fields.length) * 100).toFixed(1);
                        return (
                          <Grid item xs={12} sm={6} md={4} key={type}>
                            <Box
                              sx={{
                                p: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                              }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                {type}
                              </Typography>
                              <Typography variant="h6">
                                {count} ({percentage}%)
                              </Typography>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

            <Backdrop
              sx={{
                color: "#fff",
                zIndex: (theme) => theme.zIndex.drawer + 1,
              }}
              open={isLoading}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default Settings;
